import * as React from 'react'
import { useCallback, useState, useMemo } from 'react'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { useTheme } from '@mui/material/styles'
import { ClosingTypeVariant } from '../ClosingRequirements/typings'
import { ClosingRequrementsDialog } from './ClosingRequirementsDialog'
import { SettingsRow } from './SettingsRow'
import { LeadingIcon } from './LeadIcon'

interface Props {
  description?: string
  onClick?: () => void
  path?: string
  title: string
  icon: React.ReactNode
  dataTestId?: string
}

export function SettingsHomeSubSectionClosingRequirements(props: Props) {
  const { description, title, dataTestId, icon } = props
  const theme = useTheme()

  const mode = useMemo<ClosingTypeVariant | undefined>(() => {
    switch (title) {
      case 'Agreements':
        return 'agreement'
      case 'Scheduling':
        return 'scheduling'
      case 'Capture Card':
        return 'card'
      default:
        return undefined
    }
  }, [title])

  const [openPanel, setOpenPanel] = useState<ClosingTypeVariant | undefined>(
    undefined,
  )

  const onClose = useCallback(() => setOpenPanel(undefined), [])
  const onOpen = useCallback(() => {
    if (mode) setOpenPanel(mode)
  }, [mode])

  return (
    <>
      <SettingsRow
        left={
          <Stack
            direction="row"
            alignItems="center"
            gap={{ xs: 1, sm: 2 }}
            sx={{ minWidth: 0 }}
          >
            <LeadingIcon>{icon}</LeadingIcon>

            <Stack gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
              {/* Title can wrap to a second line if needed */}
              <Typography
                variant="h6"
                onClick={onOpen}
                data-test-id={dataTestId}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    cursor: mode ? 'pointer' : 'default',
                    textDecoration: mode ? 'underline' : 'none',
                  },
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {title}
              </Typography>

              {description ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    whiteSpace: 'normal', // ✅ wrap freely
                    wordBreak: 'break-word', // handle long words/URLs
                  }}
                >
                  {description}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        }
        right={
          <Button
            disableElevation
            variant="outlined"
            onClick={onOpen}
            size="small" // tighter on mobile
            sx={{
              px: { xs: 1.5, sm: 2 }, // comfortable touch target
              minWidth: { xs: 88, sm: 100 },
            }}
            disabled={!mode}
          >
            Manage
          </Button>
        }
      />

      <ClosingRequrementsDialog
        mode={openPanel as ClosingTypeVariant}
        onClose={onClose}
      />
    </>
  )
}

SettingsHomeSubSectionClosingRequirements.displayName =
  'SettingsHomeSubSectionClosingRequirements'
